import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../../../constants/api";
import { API_ENDPOINTS } from "../../../constants/endpoints";
import generateLeaderboardPDF from "../../../utils/generateLeaderboardPDF";
import { RootState } from "../../../store";
import { formatLocalizedDate, formatLocalizedTime } from "../../../utils/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReportModal } from "@/components/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const MEDAL: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

interface SubtopicScore {
  subtopic: string;
  score: number;
}

interface Participant {
  _id: string;
  rank: number;
  name: string;
  designation: string;
  reference_id: string;
  cli_name: string | null;
  avgScore: number;
  totalQuizzes: number;
  subtopicScores: SubtopicScore[];
}

interface CertificationRow {
  id: string;
  serial_no: number;
  attempt_number: number;
  date_of_test: string;
  candidate_name: string;
  designation_hq: string;
  cli_name: string | null;
  selected_cli_name: string | null;
  assigned_cli_name: string | null;
  score_percentage: number;
  certificate_no: string;
  certificate_available: boolean;
  due_date: string | null;
  is_passed: boolean;
}

interface CertificationSection {
  quiz_id: string;
  quiz_name: string;
  topic_name: string;
  subtopic_name: string;
  due_date: string | null;
  passing_percentage: number;
  rows: CertificationRow[];
}

const getRankLabel = (rank: number) => (MEDAL[rank] ? `${MEDAL[rank]} ${rank}` : rank);

const Participants = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"leaderboard" | "certification">("leaderboard");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [certificationSections, setCertificationSections] = useState<CertificationSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [certificationLoading, setCertificationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { token, profile } = useSelector((state: RootState) => state.account);
  const isAdmin = profile?.role?.toUpperCase() === "ADMIN";

  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      const limit = 10;
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (searchQuery) params.append("search", searchQuery);

      const response = await axios.get(
        `${baseUrl}/api/admin/participants?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setParticipants(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error("Error fetching participants:", error);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, token]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  };

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const fetchCertificationLeaderboard = useCallback(async () => {
    try {
      setCertificationLoading(true);
      const response = await axios.get(
        `${baseUrl}${API_ENDPOINTS.GET_CERTIFICATION_LEADERBOARD}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCertificationSections(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching certification leaderboard:", error);
    } finally {
      setCertificationLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "certification") {
      fetchCertificationLeaderboard();
    }
  }, [activeTab, fetchCertificationLeaderboard]);

  const handleDownloadReport = useCallback(async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${baseUrl}${API_ENDPOINTS.GET_LEADERBOARD_DATA}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const allParticipants: Participant[] = response.data.data;
        console.log("Fetched leaderboard data:", allParticipants.length, "participants");

        const allSubtopics = Array.from(
          new Set(allParticipants.flatMap((p) => (p.subtopicScores || []).map((t) => t.subtopic)))
        );

        console.log("Generating PDF with subtopics:", allSubtopics);
        generateLeaderboardPDF(allParticipants, allSubtopics);
      }
    } catch (error) {
      console.error("Error downloading leaderboard report:", error);
    } finally {
      setDownloading(false);
    }
  }, [token]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleCertificateDownload = async (reportId: string, certificateNo: string) => {
    try {
      const response = await fetch(`${baseUrl}${API_ENDPOINTS.DOWNLOAD_CERTIFICATE}/${reportId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download certificate");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${certificateNo || "certificate"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      pages.push(<button key={1} onClick={() => handlePageChange(1)} className={`page-btn ${page === 1 ? "active" : ""}`}>1</button>);
      if (start > 2) pages.push(<span key="d1" className="page-dots">…</span>);
    }
    for (let i = start; i <= end; i++) {
      pages.push(<button key={i} className={`page-btn ${page === i ? "active" : ""}`} onClick={() => handlePageChange(i)}>{i}</button>);
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="d2" className="page-dots">…</span>);
      pages.push(<button key={totalPages} onClick={() => handlePageChange(totalPages)} className={`page-btn ${page === totalPages ? "active" : ""}`}>{totalPages}</button>);
    }

    return (
      <div className="pagination-container">
        <button className="page-nav-btn" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>‹</button>
        {pages}
        <button className="page-nav-btn" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>›</button>
      </div>
    );
  };

  const subtopicHeaders = Array.from(
    new Set(participants.flatMap((p) => (p.subtopicScores || []).map((t) => t.subtopic)))
  );
  const headers = subtopicHeaders.length > 0 ? subtopicHeaders : [];


  return (
    <div className="participants-page">

      <div className="participants-page-header">
        <h2 className="page-title">Participants</h2>
        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => setIsReportModalOpen(true)}
            className="gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Report
          </Button>
        )}
      </div>
      <ReportModal open={isReportModalOpen} onOpenChange={setIsReportModalOpen} />


      <div className="participants-search-row">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className={`leaderboard-tab-btn ${activeTab === "leaderboard" ? "leaderboard-tab-btn--active" : ""}`}
            onClick={() => setActiveTab("leaderboard")}
          >
            Leaderboard
          </Button>
          <Button
            variant="ghost"
            className={`leaderboard-tab-btn ${activeTab === "certification" ? "leaderboard-tab-btn--active leaderboard-tab-btn--certification" : ""}`}
            onClick={() => setActiveTab("certification")}
          >
            Certification
          </Button>
        </div>
        {activeTab === "leaderboard" && (
        <div className="search-input-wrapper relative">
          <svg className="search-icon absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <Input
            type="text"
            className="pl-10 pr-9 participants-search-input"
            placeholder="Search by Name or HRMS ID…"
            value={searchInput}
            onChange={handleSearchChange}
          />
          {searchInput && (
            <button className="search-clear-btn absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={clearSearch} aria-label="Clear search">✕</button>
          )}
        </div>
        )}
        {activeTab === "leaderboard" && searchQuery && (
          <span className="search-results-label text-sm text-muted-foreground">
            {loading ? "Searching…" : `${participants.length === 0 ? "No" : participants.length} result${participants.length !== 1 ? "s" : ""} for "${searchQuery}"`}
          </span>
        )}
      </div>

      {activeTab === "leaderboard" ? (
      <div className="participants-table-scroll-wrapper">
        <div className="participants-table-container">
          <Table className="participants-table">
            <TableHeader>
              <TableRow>
                <TableHead className="rank-col-head">Rank</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>HRMS ID</TableHead>
                <TableHead>CLI Name</TableHead>
                <TableHead>Result %</TableHead>
                <TableHead>Total Quizzes</TableHead>
                {headers.map((h, i) => <TableHead key={i}>{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6 + headers.length} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : participants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6 + headers.length} className="text-center py-8 text-muted-foreground">
                    No participants found{searchQuery ? ` for "${searchQuery}"` : ""}.
                  </TableCell>
                </TableRow>
              ) : participants.map((user) => (
                <TableRow
                  key={user._id}
                  className={`cursor-pointer hover:bg-muted/50 ${user.rank <= 3 ? `rank-row rank-row--${user.rank}` : ""}`}
                  onClick={() => navigate(`/participants/${user._id}`)}
                >
                  <TableCell className="rank-col-cell">
                    <span className="rank-number">
                      {MEDAL[user.rank] ? `${MEDAL[user.rank]} ${user.rank}` : user.rank}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="user-details">
                      <span className="user-name">{user.name}</span>
                      <span className="user-designation">{user.designation}</span>
                    </div>
                  </TableCell>
                  <TableCell>{user.reference_id || "—"}</TableCell>
                  <TableCell>{user.cli_name || "—"}</TableCell>
                  <TableCell><span className="score-badge">{user.avgScore}%</span></TableCell>
                  <TableCell>{user.totalQuizzes}</TableCell>
                  {headers.map((header, i) => {
                    const ts = (user.subtopicScores || []).find((t) => t.subtopic === header);
                    return <TableCell key={i}>{ts ? `${ts.score}%` : "—"}</TableCell>;
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && participants.length > 0 && renderPagination()}
        </div>
      </div>
      ) : (
        <div className="space-y-6">
          {certificationLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading certification results…</div>
          ) : certificationSections.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No certification quiz results found.</div>
          ) : (
            certificationSections.map((section) => (
              <div key={section.quiz_id} className="participants-table-container">
                <div className="px-6 pt-6 pb-4 border-b border-border">
                  <h3 className="text-xl font-semibold m-0">{section.quiz_name}</h3>
                  <p className="text-sm text-muted-foreground mt-2 mb-0">
                    {section.topic_name}{section.subtopic_name ? ` / ${section.subtopic_name}` : ""}{" "}
                    • Passing Mark {section.passing_percentage}%
                  </p>
                </div>
                <Table className="participants-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="rank-col-head">Rank</TableHead>
                      <TableHead>Date of Test</TableHead>
                      <TableHead>Name of Staff</TableHead>
                      <TableHead>Desg/HQ</TableHead>
                      <TableHead>Conducting CLI</TableHead>
                      <TableHead>Result %</TableHead>
                      <TableHead>Certificate No</TableHead>
                      <TableHead>Certificate</TableHead>
                      <TableHead>Due Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No certification attempts for this quiz yet.
                        </TableCell>
                      </TableRow>
                    ) : section.rows.map((row) => (
                      <TableRow key={row.id} className={row.serial_no <= 3 ? `rank-row rank-row--${row.serial_no}` : ""}>
                        <TableCell className="rank-col-cell">
                          <span className="rank-number">{getRankLabel(row.serial_no)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="datetime-cell">
                            <span className="datetime-cell__date">{formatLocalizedDate(row.date_of_test)}</span>
                            <span className="datetime-cell__time">{formatLocalizedTime(row.date_of_test)}</span>
                          </div>
                        </TableCell>
                        <TableCell>{row.candidate_name}</TableCell>
                        <TableCell>{row.designation_hq || "—"}</TableCell>
                        <TableCell>{row.selected_cli_name || row.cli_name || "—"}</TableCell>
                        <TableCell><span className="score-badge">{Math.round(row.score_percentage || 0)}%</span></TableCell>
                        <TableCell>{row.certificate_no || "—"}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            disabled={!row.certificate_available}
                            onClick={() => handleCertificateDownload(row.id, row.certificate_no)}
                          >
                            Download
                          </Button>
                        </TableCell>
                        <TableCell>{formatLocalizedDate(row.due_date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Participants;
